// RightPanelBody.jsx
import React, { useState, useMemo, useCallback } from "react";
import AtonTable from "./Atontable";
import AtonRadiusTable from "./Atonradiustable";
import BridgeTable from "./Bridgetable";
import CableTable from "./Cabletable";
import { useNumberField } from "./useNumberField";

// datetime-local 기본값: 현재 시간(분까지)
function nowLocalForInput() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  const yyyy = d.getFullYear();
  const mm = pad(d.getMonth() + 1);
  const dd = pad(d.getDate());
  const hh = pad(d.getHours());
  const mi = pad(d.getMinutes());
  return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
}

export default function RightPanelBody({
  // 공통
  defaultTab = "turn",
  onTabChange,
  renderTurn,
  renderDohc,
  onRowDoubleClick,

  // AtoN 관련
  atons,

  // 선회반경 API (#1) + 원형검색 API (#2)
  onRunTurn,                 // ({ ids }) => void
  onSearchAtonsByCircle,     // ({ latitude, longitude, radius }) => void
  searchResults = [],
  searchLoading = false,
  searchErr = null,
  onClearSearchResults,      // () => void
  onclearRunTurn,            // () => void

  // DOHC 목록 (부모에서 GET)
  bridges = [],
  bridgesLoading = false,
  bridgesErr = null,
  cables = [],
  cablesLoading = false,
  cablesErr = null,

  // DOHC 실행/초기화 (부모 map.jsx에서 내려줌)
  onRunBridgeDohc,           // ({ ids, structure_proximity_radius, air_draft, passage_time, clear, caution, no_access })
  onRunCableDohc,            // (동일 시그니처)
  onClearBridgeDohc,
  onClearCableDohc,
}) {
  const [tab, setTab] = useState(defaultTab);

  // ----------- 선회반경 탭(AtoN) -----------
  const [selectedAtons, setSelectedAtons] = useState([]);

  // 🔽 숫자 입력을 모두 훅으로 관리 (빈값 "" 허용)
  const qLat       = useNumberField(34.78773);
  const qLng       = useNumberField(126.35226);
  const qRadius    = useNumberField(1000);

  // --------- 교량 DOHC ----------
  const [selectedBridgeIds, setSelectedBridgeIds] = useState([]);
  const [bridgePassageTime, setBridgePassageTime] = useState(nowLocalForInput());
  const bridgeRadius    = useNumberField(500);
  const bridgeAirDraft  = useNumberField(10);
  const bridgeClear     = useNumberField(2);
  const bridgeCaution   = useNumberField(2);
  const bridgeNoAccess  = useNumberField(1);

  // --------- 케이블 DOHC ----------
  const [selectedCableIds, setSelectedCableIds] = useState([]);
  const [cablePassageTime, setCablePassageTime] = useState(nowLocalForInput());
  const cableRadius     = useNumberField(500);
  const cableAirDraft   = useNumberField(10);
  const cableClear      = useNumberField(2);
  const cableCaution    = useNumberField(2);
  const cableNoAccess   = useNumberField(1);

  // AtoN 선택
  const handleChangeSelectedAtons = useCallback((ids) => {
    setSelectedAtons((prev) => (prev.join("|") === ids.join("|") ? prev : ids));
  }, []);

  const handleRowDoubleClick = useCallback(
    (a) => { onRowDoubleClick?.(a); },
    [onRowDoubleClick]
  );

  // 좌표+반경 검색
  const doSearch = useCallback(() => {
    const lat = qLat.number;
    const lng = qLng.number;
    const rad = qRadius.number;

    if (lat == null || lng == null || rad == null) return; // 빈값이면 실행 안 함
    onSearchAtonsByCircle?.({
      latitude: lat,
      longitude: lng,
      radius: rad,
    });
  }, [onSearchAtonsByCircle, qLat.number, qLng.number, qRadius.number]);

  const onResetRunTurn = useCallback(() => {
    setSelectedAtons([]);
    onclearRunTurn?.();
  }, [onclearRunTurn]);

  const onResetSearch = useCallback(() => {
    qLat.setRaw(String(34.78773));
    qLng.setRaw(String(126.35226));
    qRadius.setRaw(String(1000));
    onClearSearchResults?.();
  }, [qLat, qLng, qRadius, onClearSearchResults]);

  // 교량/케이블 선택
  const handleChangeSelectedBridges = useCallback((ids) => {
    setSelectedBridgeIds(prev => (prev.join("|") === ids.join("|") ? prev : ids));
  }, []);
  const handleChangeSelectedCables = useCallback((ids) => {
    setSelectedCableIds(prev => (prev.join("|") === ids.join("|") ? prev : ids));
  }, []);

  // 교량 DOHC 실행/초기화
  const doRunBridge = useCallback(() => {
    onRunBridgeDohc?.({
      ids: selectedBridgeIds.map(Number),
      structure_proximity_radius: bridgeRadius.number,
      air_draft: bridgeAirDraft.number,
      passage_time: bridgePassageTime, // "YYYY-MM-DDTHH:mm"
      clear: bridgeClear.number,
      caution: bridgeCaution.number,
      no_access: bridgeNoAccess.number,
    });
  }, [
    onRunBridgeDohc,
    selectedBridgeIds,
    bridgeRadius.number,
    bridgeAirDraft.number,
    bridgePassageTime,
    bridgeClear.number,
    bridgeCaution.number,
    bridgeNoAccess.number,
  ]);

  const doResetBridge = useCallback(() => {
    setSelectedBridgeIds([]);
    setBridgePassageTime(nowLocalForInput());
    bridgeRadius.setRaw(String(500));
    bridgeAirDraft.setRaw(String(10));
    bridgeClear.setRaw(String(2));
    bridgeCaution.setRaw(String(2));
    bridgeNoAccess.setRaw(String(1));
    onClearBridgeDohc?.();
  }, [bridgeRadius, bridgeAirDraft, bridgeClear, bridgeCaution, bridgeNoAccess, onClearBridgeDohc]);

  // 케이블 DOHC 실행/초기화
  const doRunCable = useCallback(() => {
    onRunCableDohc?.({
      ids: selectedCableIds.map(Number),
      structure_proximity_radius: cableRadius.number,
      air_draft: cableAirDraft.number,
      passage_time: cablePassageTime,
      clear: cableClear.number,
      caution: cableCaution.number,
      no_access: cableNoAccess.number,
    });
  }, [
    onRunCableDohc,
    selectedCableIds,
    cableRadius.number,
    cableAirDraft.number,
    cablePassageTime,
    cableClear.number,
    cableCaution.number,
    cableNoAccess.number,
  ]);

  const doResetCable = useCallback(() => {
    setSelectedCableIds([]);
    setCablePassageTime(nowLocalForInput());
    cableRadius.setRaw(String(500));
    cableAirDraft.setRaw(String(10));
    cableClear.setRaw(String(2));
    cableCaution.setRaw(String(2));
    cableNoAccess.setRaw(String(1));
    onClearCableDohc?.();
  }, [cableRadius, cableAirDraft, cableClear, cableCaution, cableNoAccess, onClearCableDohc]);

  // 탭 + 키보드 내비
  const tabs = useMemo(
    () => [
      { key: "turn", label: "선회반경" },
      { key: "dohc", label: "DOHC" },
    ],
    []
  );
  const changeTab = useCallback((next) => {
    setTab(next);
    onTabChange?.(next);
  }, [onTabChange]);
  const onKeyNav = useCallback((e) => {
    const idx = tabs.findIndex((t) => t.key === tab);
    if (e.key === "ArrowRight") {
      e.preventDefault();
      changeTab(tabs[(idx + 1) % tabs.length].key);
    } else if (e.key === "ArrowLeft") {
      e.preventDefault();
      changeTab(tabs[(idx - 1 + tabs.length) % tabs.length].key);
    }
  }, [tab, tabs, changeTab]);

  return (
    <div className="text-left">
      {/* 탭 */}
      <div
        role="tablist"
        aria-label="데이터 유형 선택"
        style={{ display: "flex", gap: 16, paddingBottom: 8, borderBottom: "1px solid #e5e7eb" }}
      >
        {tabs.map((t) => {
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={active}
              aria-controls={`panel-${t.key}`}
              id={`tab-${t.key}`}
              onClick={() => changeTab(t.key)}
              onKeyDown={onKeyNav}
              style={{
                background: "transparent",
                border: "none",
                outline: "none",
                padding: "6px 0",
                margin: 0,
                color: active ? "#111827" : "#6b7280",
                borderBottom: active ? "2px solid #337ab7" : "2px solid transparent",
                cursor: "pointer",
                fontSize: 14,
                fontWeight: 600,
              }}
            >
              {t.label}
            </button>
          );
        })}
      </div>

      <div className="pt-3">
        {/* ───────── 선회반경 ───────── */}
        <section role="tabpanel" id="panel-turn" aria-labelledby="tab-turn" hidden={tab !== "turn"}>
          {renderTurn ? (
            renderTurn()
          ) : (
            <>
              <h4 className="text-sm font-semibold">선회반경 가시화 설정</h4>
              <div className="text-sm text-gray-600 dark:text-gray-300">
                항로표지 목록과 파라미터 입력에 따라 선회반경의 계산 결과를 제공합니다.
              </div>
            </>
          )}

          <div style={{ paddingTop: 12, borderTop: "1px solid #e5e7eb" }} />

          {/* 선택 목록 */}
          <div style={{ padding: 12 }}>
            <AtonTable
              atons={atons}
              selectedIds={selectedAtons}
              onChangeSelected={handleChangeSelectedAtons}
              onRowDoubleClick={handleRowDoubleClick}
            />
          </div>

          {/* 선택 가시화/초기화 (버튼 위치 유지) */}
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 8,
              marginTop: -20,
              marginBottom: 5,
              padding: 12,
            }}
          >
            <button
              className="rounded bg-blue-600 px-3 py-2 text-white disabled:opacity-50"
              onClick={() => onRunTurn?.({ ids: selectedAtons })}
            >
              선회반경 가시화
            </button>
            <button
              className="rounded bg-blue-200 px-3 py-2 text-white disabled:opacity-50"
              onClick={onResetRunTurn}
            >
              초기화
            </button>
          </div>

          <div style={{ paddingTop: 12, borderTop: "1px solid #e5e7eb" }} />

          {/* 좌표+반경 검색 입력 */}
          <div
            style={{
              padding: 12,
              display: "grid",
              gridTemplateColumns: "1fr 1fr 1fr",
              gap: 8,
              alignItems: "end",
            }}
          >
            <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <span style={{ fontSize: 12, color: "#6b7280" }}>위도 (lat)</span>
              <input
                className="border rounded px-2 py-1"
                step="0.000001"
                {...qLat.bind}
              />
            </label>
            <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <span style={{ fontSize: 12, color: "#6b7280" }}>경도 (lng)</span>
              <input
                className="border rounded px-2 py-1"
                step="0.000001"
                {...qLng.bind}
              />
            </label>
            <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <span style={{ fontSize: 12, color: "#6b7280" }}>반경 (m)</span>
              <input
                className="border rounded px-2 py-1"
                step="1"
                {...qRadius.bind}
              />
            </label>
          </div>
          <div style={{ marginTop: -10, padding: 12, textAlign: "right" }}>
            <button
              className="rounded bg-blue-600 px-3 py-2 text-white disabled:opacity-50"
              onClick={doSearch}
              style={{ marginRight: 8 }}
            >
              항로표지 검색
            </button>
            <button
              className="rounded bg-blue-200 px-3 py-2 text-white disabled:opacity-50"
              onClick={onResetSearch}
            >
              초기화
            </button>
          </div>

          {/* 검색 로딩/에러 */}
          {searchLoading && <div style={{ color: "#2563eb", padding: "0 12px" }}>검색 중…</div>}
          {searchErr && <div style={{ color: "crimson", padding: "0 12px" }}>에러: {searchErr}</div>}

          {/* 검색 결과 테이블 */}
          <div style={{ padding: 12 }}>
            <AtonRadiusTable
              atons={searchResults}
              totalCount={atons?.length ?? 0}
              height={280}
              onRowDoubleClick={(row) =>
                onRowDoubleClick?.({ lat: row.lat, lng: row.lng, name_ko: row.name })
              }
            />
          </div>
        </section>

        {/* ───────── DOHC ───────── */}
        <section role="tabpanel" id="panel-dohc" aria-labelledby="tab-dohc" hidden={tab !== "dohc"}>
          {renderDohc ? (
            renderDohc()
          ) : (
            <>
              <h4 className="text-sm font-semibold">DOHC 가시화 설정</h4>
              <div className="text-sm text-gray-600 dark:text-gray-300">
                교량 및 케이블 목록과 파라미터 입력에 따라 DOHC의 계산 결과를 제공합니다.
              </div>
            </>
          )}

          {/* 교량 */}
          <div style={{ paddingTop: 12, borderTop: "1px solid #e5e7eb" }} />
          <div style={{ padding: 12 }}>
            {bridgesLoading && <div style={{ color: "#2563eb" }}>교량 목록 불러오는 중…</div>}
            {bridgesErr && <div style={{ color: "crimson" }}>교량 에러: {bridgesErr}</div>}
            <BridgeTable
              items={bridges}
              selectedIds={selectedBridgeIds}
              onChangeSelected={handleChangeSelectedBridges}
              onRowDoubleClick={(row) =>
                onRowDoubleClick?.({ lat: row.lat, lng: row.lng, name_ko: row.name })
              }
            />
          </div>

          {/* 교량 파라미터 */}
          <div
            style={{
              marginTop: -10,
              padding: 12,
              display: "grid",
              gridTemplateColumns: "1.2fr 1fr 1fr",
              gap: 8,
              alignItems: "end",
            }}
          >
            <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <span style={{ fontSize: 12, color: "#6b7280" }}>통과시간</span>
              <input
                type="datetime-local"
                value={bridgePassageTime}
                onChange={(e) => setBridgePassageTime(e.target.value)}
                className="border rounded px-2 py-1"
              />
            </label>
            <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <span style={{ fontSize: 12, color: "#6b7280" }}>반경 (m)</span>
              <input
                className="border rounded px-2 py-1"
                step="1"
                {...bridgeRadius.bind}
              />
            </label>
          </div>
          <div
            style={{
              marginTop: -20,
              padding: 12,
              display: "grid",
              gridTemplateColumns: "1fr 1fr 1fr",
              gap: 8,
              alignItems: "end",
            }}
          >
            <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <span style={{ fontSize: 12, color: "#6b7280" }}>Air Draft (m)</span>
              <input
                className="border rounded px-2 py-1"
                step="0.1"
                {...bridgeAirDraft.bind}
              />
            </label>
            <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <span style={{ fontSize: 12, color: "#6b7280" }}>Clear (m)</span>
              <input
                className="border rounded px-2 py-1"
                step="0.1"
                {...bridgeClear.bind}
              />
            </label>
            <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <span style={{ fontSize: 12, color: "#6b7280" }}>No Access (m)</span>
              <input
                className="border rounded px-2 py-1"
                step="0.1"
                {...bridgeNoAccess.bind}
              />
            </label>
          </div>

          {/* 버튼 (위치 유지) */}
          <div style={{ marginTop: -10, padding: 12, textAlign: "right" }}>
            <button
              className="rounded bg-blue-600 px-3 py-2 text-white disabled:opacity-50"
              onClick={doRunBridge}
              style={{ marginRight: 8 }}
            >
              교량 DOHC 가시화
            </button>
            <button
              className="rounded bg-blue-200 px-3 py-2 text-white disabled:opacity-50"
              onClick={doResetBridge}
            >
              초기화
            </button>
          </div>

          {/* 케이블 */}
          <div style={{ paddingTop: 12, borderTop: "1px solid #e5e7eb" }} />
          <div style={{ padding: 12 }}>
            {cablesLoading && <div style={{ color: "#2563eb" }}>케이블 목록 불러오는 중…</div>}
            {cablesErr && <div style={{ color: "crimson" }}>케이블 에러: {cablesErr}</div>}
            <CableTable
              items={cables}
              selectedIds={selectedCableIds}
              onChangeSelected={handleChangeSelectedCables}
              onRowDoubleClick={(row) =>
                onRowDoubleClick?.({ lat: row.lat, lng: row.lng, name_ko: row.name })
              }
            />
          </div>

          {/* 케이블 파라미터 */}
          <div
            style={{
              marginTop: -10,
              padding: 12,
              display: "grid",
              gridTemplateColumns: "1.2fr 1fr 1fr",
              gap: 8,
              alignItems: "end",
            }}
          >
            <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <span style={{ fontSize: 12, color: "#6b7280" }}>통과시간</span>
              <input
                type="datetime-local"
                value={cablePassageTime}
                onChange={(e) => setCablePassageTime(e.target.value)}
                className="border rounded px-2 py-1"
              />
            </label>
            <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <span style={{ fontSize: 12, color: "#6b7280" }}>반경 (m)</span>
              <input
                className="border rounded px-2 py-1"
                step="1"
                {...cableRadius.bind}
              />
            </label>
          </div>
          <div
            style={{
              marginTop: -20,
              padding: 12,
              display: "grid",
              gridTemplateColumns: "1fr 1fr 1fr",
              gap: 8,
              alignItems: "end",
            }}
          >
            <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <span style={{ fontSize: 12, color: "#6b7280" }}>Air Draft (m)</span>
              <input
                className="border rounded px-2 py-1"
                step="0.1"
                {...cableAirDraft.bind}
              />
            </label>
            <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <span style={{ fontSize: 12, color: "#6b7280" }}>Clear (m)</span>
              <input
                className="border rounded px-2 py-1"
                step="0.1"
                {...cableClear.bind}
              />
            </label>
            <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <span style={{ fontSize: 12, color: "#6b7280" }}>No Access (m)</span>
              <input
                className="border rounded px-2 py-1"
                step="0.1"
                {...cableNoAccess.bind}
              />
            </label>
          </div>

          {/* 버튼 (위치 유지) */}
          <div style={{ marginTop: -10, padding: 12, textAlign: "right" }}>
            <button
              className="rounded bg-blue-600 px-3 py-2 text-white disabled:opacity-50"
              onClick={doRunCable}
              style={{ marginRight: 8 }}
            >
              케이블 DOHC 가시화
            </button>
            <button
              className="rounded bg-blue-200 px-3 py-2 text-white disabled:opacity-50"
              onClick={doResetCable}
            >
              초기화
            </button>
          </div>
        </section>
      </div>
    </div>
  );
}
